import React from "react";
import { makeStyles } from "@material-ui/styles";
import { Button } from "@material-ui/core";
import PropTypes from "prop-types";
import cx from "classnames";

function getBgColor({ props, theme }) {
  const { bgColor, secondary, transparent } = props;
  if (bgColor) return bgColor;
  if (transparent) return "#fff";
  return secondary
    ? theme.palette.colors.secondary
    : theme.palette.primary.main;
}

function getButtonHeight({ props }) {
  if (props.height) return props.height;
  switch (props.variant) {
    case "large":
      return "40px";
    case "medium":
      return "36px";
    case "small":
      return "32px";
    default:
      return "40px";
  }
}

function getFontSize({ props }) {
  switch (props.variant) {
    case "large":
      return "17px";
    case "medium":
      return "15px";
    case "small":
      return "13px";
    default:
      return "17px";
  }
}

function getColor({ props, theme }) {
  const { transparent, color, secondary } = props;
  if (color) return color;
  if (transparent) {
    if (secondary) return theme.palette.colors.secondary;
    return theme.palette.primary.main;
  }
  return "#fff";
}

function getPaddings({ props }) {
  const { variant } = props;
  switch (variant) {
    case "large":
      return "10px 20px";
    case "medium":
      return "8px 16px";

    case "small":
      return "8px 12px";

    default:
      return "10px 20px";
  }
}

function getDisabledBgColor({ props }) {
  if (props.transparent) return "#fff";
  return "#B2D4FC";
}
function getDisabledColor({ props }) {
  if (props.transparent) return "#B2D4FC";
  return "#fff";
}

function getImageContainer({ props, isAfter }) {
  let container = {};
  if (props.icon) {
    container = {
      content: `""`,
      backgroundImage: `url(${props.icon})`,
      height: "16px",
      width: "16px",
      marginRight: "6px",
      backgroundPosition: "center",
      backgroundSize: "cover",
      position: "relative",
      top: "1px"
    };

    if (isAfter) {
      container = { ...container, marginRight: "0px", marginLeft: "6px" };
    }
    if (props.variant === "small") {
      container = {
        ...container,
        height: "14px",
        width: "14px",
        marginRight: "4px"
      };

      if (isAfter) {
        container.marginLeft = "4px";
        container.marginRight = "0px";
      }
    }
  }
  return container;
}

const useStyle = makeStyles(theme => ({
  buttonStyle: {
    ...theme.typography.defaultFont,
    fontWeight: 700,
    borderRadius: "18px",
    color: "#FFFFFF",
    boxShadow: "none",
    margin: "10px 0",
    "&:disabled": {
      color: "#ffffff",
      backgroundColor: "rgba(42, 137, 246, 0.4)"
    }
  },
  buttonText: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  btnV1: {
    height: "40px",
    borderRadius: "100px",
    fontSize: "17px",
    padding: "20px",
    margin: "0"
  },
  secondary: {
    color: "#2a89f6",
    backgroundColor: "white",
    border: "1px solid #2a89f6",
    fontWeight: "600",
    "&:hover": {
      backgroundColor: "white",
      color: "#2a89f6"
    }
  },
  btnV2: props => ({
    position: "relative",
    fontWeight: props.fontWeight || "700",
    fontSize: props.fontSize || getFontSize({ props }),
    lineHeight: "20px",
    color: getColor({ props, theme }),
    backgroundColor: getBgColor({ props, theme }),
    outline: "none",
    borderRadius: "100px",
    height: getButtonHeight({ props }),
    width: props.width || "",
    border: "none",
    cursor: "pointer",
    padding: getPaddings({ props }),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid",
    fontFamily: "inherit",
    userSelect: "none",
    margin: "0",
    "&:disabled": {
      color: props.primary ? getDisabledColor({ props }) : "",
      backgroundColor: props.primary ? getDisabledBgColor({ props }) : "",
      pointerEvents: "none",
      opacity: !props.primary ? "0.5" : "1"
    },
    "&:before":
      props.iconPosition !== "head" ? { ...getImageContainer({ props }) } : {},
    "&:after":
      props.iconPosition === "head"
        ? { ...getImageContainer({ props, isAfter: true }) }
        : {}
  })
}));

const PrimaryButton = React.forwardRef(
  (
    {
      buttonText,
      onClick,
      className,
      disabled = false,
      v1,
      secondary,
      id,
      type = "submit",
      styles,
      v2,
      children,
      componentIcon,
      ...rest
    },
    ref
  ) => {
    const classes = useStyle({ ...rest, disabled, secondary, disabled });
    if (v2) {
      return (
        <button
          disabled={disabled}
          ref={ref}
          className={cx(classes.btnV2)}
          id={id}
          type={type}
          onClick={onClick}
        >
          {buttonText || children}
        </button>
      );
    }
    return (
      <Button
        color="primary"
        variant="contained"
        className={cx(
          classes.buttonStyle,
          className,
          v1 ? classes.btnV1 : "",
          secondary ? classes.secondary : "",
          styles
        )}
        onClick={onClick}
        disabled={disabled}
        id={id}
        type={type}
      >
        {componentIcon?<span className="flex mr-1">{componentIcon}</span>:null}
        {buttonText}
      </Button>
    );
  }
);

PrimaryButton.propTypes = {
  classes: PropTypes.object,
  buttonText: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  v2: PropTypes.bool,
  variant: PropTypes.oneOf(["large", "medium", "small"]),
  primary: PropTypes.bool,
  secondary: PropTypes.bool,
  bgColor: PropTypes.string,
  transparent: PropTypes.bool,
  color: PropTypes.string,
  iconPosition: PropTypes.oneOf(["head"]),
  height: PropTypes.string,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.string,
  width: PropTypes.string
};

export default React.memo(PrimaryButton);
